import { useMemo } from 'react';
import { mockVacationRentals } from './mockData';
import type { VacationRentalAreaData, VacationRentalTag } from '@shared/components/VacationRentalTile/VacationRentalTile.types';
import { useTranslation } from '@shared/core';

const parseVacationRentalFeatures = (whyThisZoneIsAGreatPick?: string | null): ReadonlyArray<string> => {
  return whyThisZoneIsAGreatPick?.split('\n').filter(Boolean) ?? [];
};

const parseBestForLabel = (
  bestForTags: ReadonlyArray<VacationRentalTag>,
  labelMap: Readonly<Record<VacationRentalTag, { emoji: string; label: string }>>,
): Readonly<{ emoji: string; label: string }> | undefined => {
  return bestForTags.length > 0 ? labelMap[bestForTags[0]] : undefined;
};

/**
 * Hook to get the vacation rentals data.
 * @todo [TRAV-2250] Replace with GraphQL query when backend ready
 */
export const useVacationRentals = (): Readonly<{
  loading: boolean;
  vacationRentals: ReadonlyArray<VacationRentalAreaData>;
}> => {
  const { tq } = useTranslation('guestVacationRentals');

  const labelMap: Readonly<Record<VacationRentalTag, { emoji: string; label: string }>> = useMemo(
    () => ({
      families: {
        emoji: '🏡',
        label: tq`Best for families`,
      },
      couples: {
        emoji: '💑',
        label: tq`Best for couples`,
      },
      art_lovers: {
        emoji: '🎨',
        label: tq`Best for art lovers`,
      },
      walkable: {
        emoji: '🚶',
        label: tq`Best for walkable areas`,
      },
    }),
    [tq],
  );

  // Source of truth for vacation rentals data with parsed fields from backend API
  const vacationRentals = useMemo(() => {
    return mockVacationRentals.map(rental => ({
      // Keep all fields from backend API
      ...rental,

      // Fields parsed from backend API with different structure
      features: parseVacationRentalFeatures(rental.whyThisZoneIsAGreatPick),
      bestForLabel: parseBestForLabel(rental.bestForTags, labelMap),
    }));
  }, [labelMap]);
  const loading = false;

  return {
    loading,
    vacationRentals,
  };
};
